import React, { useEffect, useState } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { Col, Row, Form, Spinner } from "react-bootstrap";
import { useFormik } from "formik";
import * as Yup from "yup";
import { Autocomplete } from "@mui/material";
import { toast } from "react-toastify";
import BootstrapTextField from "Common/BootstrapTextField";
import {
  addhsnCode,
  fetchhsnCodeById,
  updatehsnCode,
} from "slices/hsn-code/thunk";
import { fetchGstByDropDown } from "slices/gst/thunk";
import SubmitButton from "components/Buttons/SubmitButton";
import CancelBackButton from "components/Buttons/CancelBackButton";
import FormHeader from "components/Form&DetailsHeader/Form&DetailsHeader";
import { autocompleteDropdownStyles } from "Constants/Style";
import PageContainer from "components/PageContainer/PageContainer";

const HSNCodeForm: React.FC = () => {
  const { id } = useParams();
  const location = useLocation();
  const currentPage = location.state?.page || 1;

  const navigate = useNavigate();
  const dispatch = useDispatch<any>();
  const isEdit = Boolean(id);
  console.log("isEdit", isEdit);
  const [gstLoading, setGstLoading] = useState<boolean>(false);

  // Redux selectors
  const selectedHSNCode = useSelector(
    (state: any) => state.HSNCode?.selectedHSNCode
  );
  const isLoading = useSelector((state: any) => state.HSNCode?.loading);
  const hsnCodeError = useSelector((state: any) => state.HSNCode?.error);
  const gstError = useSelector((state: any) => state.GST?.error);

  const [gstOptions, setGstOptions] = useState<[]>([]);
  const [initialData, setInitialData] = useState({
    GST: "",
    HSNCodeName: "",
    HSNDescripton: "",
  });

  // Helper function to extract error message from error response
  const getErrorMessage = (error: any): string => {
    if (typeof error === "string") return error;
    if (error?.error) return error.error;
    if (error?.message) return error.message;
    if (error?.response?.data?.error) return error.response.data.error;
    if (error?.response?.data?.message) return error.response.data.message;
    return "Something went wrong";
  };

  // Fetch dropdown
  useEffect(() => {
    setGstLoading(true);
    dispatch(fetchGstByDropDown())
      .unwrap()
      .then((data: any) => {
        setGstOptions(data.data || []);
      })
      .catch((error: any) => {
        const errorMsg = getErrorMessage(error);
        toast.error(errorMsg);
      })
      .finally(() => setGstLoading(false));
  }, [dispatch]);

  // Fetch HSN code by id when in edit mode
  useEffect(() => {
    if (isEdit && id) {
      dispatch(fetchhsnCodeById(id)).catch((error: any) => {
        const errorMsg = getErrorMessage(error);
        toast.error(errorMsg);
      });
    }
  }, [id, isEdit, dispatch]);

  // Watch for HSN Code errors from Redux store
  // useEffect(() => {
  //   if (hsnCodeError) {
  //     const errorMsg = getErrorMessage(hsnCodeError);
  //     toast.error(errorMsg);
  //   }
  // }, [hsnCodeError]);

  // Watch for GST errors from Redux store
  useEffect(() => {
    if (gstError) {
      const errorMsg = getErrorMessage(gstError);
      toast.error(errorMsg);
    }
  }, [gstError]);

  // Update initialData when selectedHSNCode changes in Redux store
  useEffect(() => {
    if (selectedHSNCode && isEdit) {
      // Handle nested response structure: response.data.data or response.data
      const data = selectedHSNCode?.data?.data || selectedHSNCode?.data || selectedHSNCode;
      setInitialData({
        GST: data?.gst_percent_id || data.gst_id || "",
        HSNCodeName: data.hsn_code || "",
        HSNDescripton: data.description || "",
      });
    }
  }, [selectedHSNCode, isEdit]);

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      GST: initialData.GST || "",
      HSNCodeName: initialData.HSNCodeName || "",
      HSNDescripton: initialData.HSNDescripton || "",
    },
    validationSchema: Yup.object({
      GST: Yup.string().required("Please select GST"),
      HSNCodeName: Yup.string()
        .required("Please enter HSN Code"),

      // .matches(
      //   /^[A-Za-z ]+$/,
      //   "HSN Code  must contain only letters and spaces"
      // ),
      HSNDescripton: Yup.string().required("Please enter HSN Description"),
    }),
    onSubmit: async (values) => {
      const payload = {
        hsn_code: values.HSNCodeName.toString(),
        gst_percentage: values.GST,
        hsn_code_description: values.HSNDescripton,
      };

      try {
        if (isEdit) {
          await dispatch(updatehsnCode({ id, ...payload })).unwrap();
          toast.success("HSN Code updated successfully");
        } else {
          await dispatch(addhsnCode(payload)).unwrap();
          toast.success("HSN Code added successfully");
        }
        navigate("/hsn-codes", { state: { clearState: !isEdit } });
      } catch (error: any) {
        const errorMsg = getErrorMessage(error);
        toast.error(errorMsg);
      }
    },
  });

  return (
    <PageContainer>
        <FormHeader
          title={isEdit ? "Edit HSN Code" : "Add HSN Code"}
          firstLabel="HSN Code"
          secondLabel={isEdit ? "Edit HSN Code" : "Add HSN Code"}
          handleArrowClick={() => navigate("/hsn-codes")}
        />

        {isLoading && isEdit ? (
          <div className="text-center py-5">
            <Spinner animation="border" variant="primary" />
          </div>
        ) : (
          <Form autoComplete="off" onSubmit={formik.handleSubmit}>
            <Row>
              <Col lg={6}>
                <div className="mb-4">
                  <Form.Label>
                    GST% <span className="text-danger">*</span>
                  </Form.Label>
                  <Autocomplete
                    options={gstOptions}
                    loading={gstLoading}
                    loadingText={
                      <span className="d-flex align-items-center gap-2">
                        <Spinner animation="border" size="sm" /> Loading...
                      </span>
                    }
                    getOptionLabel={(option: any) =>
                      typeof option === "string"
                        ? option
                        : `${option.gst_percent}%`
                    }
                    value={
                      gstOptions.find(
                        (gst: any) => gst.gst_id === formik.values.GST
                      ) || null
                    }
                    onChange={(event, selectedGST) => {
                      formik.setFieldValue("GST", selectedGST?.gst_id || "");
                    }}
                    onBlur={() => formik.setFieldTouched("GST", true)}
                    ListboxProps={autocompleteDropdownStyles.ListboxProps}
                    renderOption={(props, option: any) =>
                      autocompleteDropdownStyles.renderOption(
                        props,
                        option,
                        (opt: any) =>
                          typeof opt === "string"
                            ? opt
                            : `${opt.gst_percent}%`
                      )
                    }
                    renderInput={(params) => (
                      <BootstrapTextField
                        {...params}
                        placeholder="Select GST"
                        name="GST"
                        error={formik.touched.GST && Boolean(formik.errors.GST)}
                        helperText={formik.touched.GST && formik.errors.GST}
                        fullWidth
                        size="small"
                        sx={autocompleteDropdownStyles.inputSx}
                      />
                    )}
                  />
                </div>
              </Col>

              <Col lg={6}>
                <div className="mb-3">
                  <Form.Label>
                    HSN Code <span className="text-danger">*</span>
                  </Form.Label>
                  <Form.Control
                    type="number"
                    name="HSNCodeName"
                    placeholder="Enter HSN Code"
                    value={formik.values.HSNCodeName}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    isInvalid={
                      !!formik.errors.HSNCodeName && formik.touched.HSNCodeName
                    }
                  />
                  <Form.Control.Feedback type="invalid">
                    {formik.errors.HSNCodeName}
                  </Form.Control.Feedback>
                </div>
              </Col>
            </Row>

            <Row>
              <Col lg={12}>
                <div className="mb-3">
                  <Form.Label>
                    HSN Code Description <span className="text-danger">*</span>
                  </Form.Label>
                  <Form.Control
                    type="text"
                    name="HSNDescripton"
                    placeholder="Enter Description"
                    value={formik.values.HSNDescripton}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    isInvalid={
                      !!formik.errors.HSNDescripton &&
                      formik.touched.HSNDescripton
                    }
                  />
                  <Form.Control.Feedback type="invalid">
                    {formik.errors.HSNDescripton}
                  </Form.Control.Feedback>
                </div>
              </Col>
            </Row>

            <div className="d-flex justify-content-center gap-3 py-3">
              <CancelBackButton to="/hsn-codes" />

              {/* Reusable submit button with spinner */}
              <SubmitButton isSubmitting={formik.isSubmitting} isEdit={isEdit} />
            </div>
          </Form>
        )}
    </PageContainer>
  );
};

export default HSNCodeForm;
